import { z } from "zod";

export const ContactKindSchema = z.enum([
  "host",
  "planner",
  "venue",
  "corporate",
  "vendor",
]);
export type ContactKind = z.infer<typeof ContactKindSchema>;

export const ContactWarmSchema = z.enum(["warm", "cold"]);
export type ContactWarm = z.infer<typeof ContactWarmSchema>;

export const CONTACT_KINDS: ContactKind[] = [
  "host",
  "planner",
  "venue",
  "corporate",
  "vendor",
];

export const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  host: "Host",
  planner: "Planner",
  venue: "Venue",
  corporate: "Corporate",
  vendor: "Vendor",
};

export const ContactSchema = z.object({
  id: z.string(),
  kind: ContactKindSchema,
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  instagram: z.string().nullable(),
  source: z.string().nullable(),
  warm: ContactWarmSchema,
  tags: z.array(z.string()),
  notes: z.string().nullable(),
  // Contact 360 (Phase 10): address fields, all nullable — most contacts
  // (especially non-host kinds) never get a full mailing address.
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  // Phase 11: consent tracking for outbound email/SMS. Optional/nullable —
  // mirrors the address-fields convention above for a field set a later
  // phase bolts onto an existing contract; default false when a backend
  // revision omits them rather than treating "unknown" as consented.
  email_consent: z.boolean().nullable().optional(),
  sms_consent: z.boolean().nullable().optional(),
  consent_source: z.string().nullable().optional(),
  consent_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
});
export type Contact = z.infer<typeof ContactSchema>;

export const ContactListResponseSchema = z.object({
  items: z.array(ContactSchema),
  next_cursor: z.string().nullable(),
});
export type ContactListResponse = z.infer<typeof ContactListResponseSchema>;

// Shared by create + edit forms. `kind`/`name`/`warm` required; everything
// else optional per contract. Empty strings from the form are normalized to
// null before the request leaves the form component (see ContactFormDialog).
export const ContactFormSchema = z.object({
  kind: ContactKindSchema,
  name: z.string().min(1, "Name is required"),
  company: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  email: z
    .union([z.string().email("Enter a valid email"), z.literal("")])
    .nullable()
    .optional(),
  instagram: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  warm: ContactWarmSchema,
  tags: z.array(z.string()),
  notes: z.string().nullable().optional(),
  address_line1: z.string().nullable().optional(),
  address_line2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  email_consent: z.boolean().optional(),
  sms_consent: z.boolean().optional(),
});
export type ContactFormValues = z.infer<typeof ContactFormSchema>;
